import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, FormControl, Input, InputAdornment, InputLabel, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import mainlogo from '../Assets/LogoPattern.png';
import { toast } from 'react-hot-toast';
import { postDatatoServer } from '../utils/apiBase';
import ContextHelper from '../ContextHooks/ContextHelper';

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');
  const [timer, setTimer] = useState(0);
  const [subscriptionDialog, setSubscriptionDialog] = useState({
    isOpen: false,
    daysExpired: 0,
    type: 'expired',
  });

  const { currentUser, setCurrentUser, storeDataInLocalStorage } = ContextHelper();
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (currentUser?.token) {
      navigate(currentUser?.isadmin ? '/add-report' : '/view-report');
    }
  }, [currentUser, navigate]);

  const getSubscriptionDelayInfo = (daysExpired) => {
    const configs = {
      1: { time: 10000, message: 'Your subscription expired yesterday', color: 'warning', severity: 'medium' },
      2: { time: 15000, message: 'Your subscription expired 2 days ago', color: 'warning', severity: 'high' },
      3: { time: 20000, message: 'Your subscription expired 3 days ago', color: 'danger', severity: 'high' },
      4: { time: 30000, message: 'Your subscription expired 4 days ago', color: 'danger', severity: 'critical' },
      5: { time: 60000, message: 'Your subscription expired 5 days ago', color: 'danger', severity: 'critical' },
    };

    const day = Math.ceil(daysExpired);
    return configs[day] || configs[5];
  };

  const handleSubscriptionDelay = async (daysExpired) => {
    const config = getSubscriptionDelayInfo(daysExpired);

    setLoaderMessage(config.message);
    setIsLoading(true);
    setSubscriptionDialog({
      isOpen: true,
      daysExpired: Math.ceil(daysExpired),
      type: 'expired',
    });

    const totalSeconds = config.time / 1000;
    setTimer(totalSeconds);

    for (let i = totalSeconds; i > 0; i--) {
      setTimer(i);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setIsLoading(false);
    setSubscriptionDialog((prev) => ({ ...prev, isOpen: false }));

    return config.message;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const handleLogin = (event) => {
    event.preventDefault();
    const data = {
      username: event.target.username.value,
      password: event.target.password.value,
    };

    let error = {};

    if (!data.username.trim()) {
      error.username = 'Please provide a valid username';
    }

    if (!data.password.trim()) {
      error.password = 'Please provide a valid password';
    }

    if (Object.keys(error).length > 0) {
      setErrorMessage(error);
      return;
    }

    setErrorMessage({});

    postDatatoServer({
      end_point: 'login',
      body: data,
      call_back: handleResponse,
    });
  };

  const handleResponse = async (res) => {
    if (res?.response && res?.status === 'success') {
      if (res?.response?.status) {
        const userData = res?.response;

        // Convert timestamp to date (expiryDate is in milliseconds)
        const expiryDate = new Date(parseInt(userData.expiryDate));
        const currentDate = new Date();
        const differenceInTime = expiryDate - currentDate;
        const differenceInDays = differenceInTime / (1000 * 60 * 60 * 24);

        // Store user data
        storeDataInLocalStorage({ key: 'current_user', value: userData });
        localStorage.setItem('token', userData.token);

        if (differenceInDays < 0) {
          // Subscription expired
          const daysExpired = Math.abs(differenceInDays);

          if (daysExpired > 5) {
            toast.error('Your subscription has expired. Please renew to continue.');
            navigate('/subscription-expired');
            return;
          }

          // Grace period - show delay
          const toastMessage = await handleSubscriptionDelay(daysExpired);
          toast.error(toastMessage);
          toast.success(userData.message);
          setCurrentUser(userData);
          navigate(userData.isadmin ? '/add-report' : '/view-report');
        } else if (differenceInDays < 3) {
          // Subscription expiring soon
          setSubscriptionDialog({
            isOpen: true,
            daysExpired: Math.ceil(differenceInDays),
            type: 'expiring',
          });
          setTimeout(() => {
            setSubscriptionDialog((prev) => ({ ...prev, isOpen: false }));
          }, 3000);
          toast.error('Your subscription is about to expire. Please renew soon!');
          toast.success(userData.message);
          setCurrentUser(userData);
          navigate(userData.isadmin ? '/add-report' : '/view-report');
        } else {
          // Valid subscription
          toast.success(userData.message);
          setCurrentUser(userData);
          navigate(userData.isadmin ? '/add-report' : '/view-report');
        }
      } else {
        toast.error(res?.response?.message);
      }
    } else {
      toast.error('Login failed. Please try again.');
    }
  };

  return (
    <>
      {/* Subscription Dialog Modal */}
      {subscriptionDialog.isOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
          onClick={(e) => e.preventDefault()}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 text-center pb-2">
                <div className="w-100">
                  <div className="d-flex justify-content-center mb-3">
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center shadow-lg`}
                      style={{
                        width: '40px',
                        height: '40px',
                        background:
                          subscriptionDialog.type === 'expiring'
                            ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                            : 'linear-gradient(135deg, #ef4444, #dc2626)',
                      }}
                    >
                      {subscriptionDialog.type === 'expiring' ? (
                        <WarningIcon style={{ fontSize: '40px', color: 'white' }} />
                      ) : (
                        <ErrorIcon style={{ fontSize: '40px', color: 'white' }} />
                      )}
                    </div>
                  </div>
                  <h4 className="modal-title fw-bold text-center">
                    {subscriptionDialog.type === 'expiring' ? 'Subscription Expiring Soon' : 'Subscription Expired'}
                  </h4>
                  <p className="text-muted text-center mt-2">
                    {subscriptionDialog.type === 'expiring'
                      ? `Your subscription expires in ${subscriptionDialog.daysExpired} day${
                          subscriptionDialog.daysExpired !== 1 ? 's' : ''
                        }`
                      : loaderMessage}
                  </p>
                </div>
              </div>

              <div className="modal-body px-4">
                {subscriptionDialog.type === 'expired' && (
                  <div className="mb-4">
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="progress" style={{ height: '16px' }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                          role="progressbar"
                          style={{
                            width:
                              timer > 0
                                ? `${
                                    (timer / (getSubscriptionDelayInfo(subscriptionDialog.daysExpired).time / 1000)) *
                                    100
                                  }%`
                                : '0%',
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <small className="text-muted">Please wait while we verify your subscription...</small>
                      <div className="d-flex align-items-center bg-light px-3 py-1 rounded-pill">
                        <AccessTimeIcon style={{ fontSize: '16px', color: '#6366f1', marginRight: '8px' }} />
                        <span className="fw-bold">{formatTime(timer)}</span>
                      </div>
                    </div>

                    {/* Grace Period Info */}
                    <div className="alert alert-info border-0" style={{ backgroundColor: '#dbeafe' }}>
                      <div className="d-flex align-items-start">
                        <StarIcon
                          style={{ fontSize: '20px', color: '#3b82f6', marginRight: '12px', marginTop: '2px' }}
                        />
                        <div>
                          <h6 className="fw-semibold mb-1">Grace Period Active</h6>
                          <small>
                            You can still access your account while we verify your subscription status. Please wait for
                            the verification to complete.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {subscriptionDialog.type === 'expiring' && (
                  <div className="alert alert-warning border-0" style={{ backgroundColor: '#fef3c7' }}>
                    <div className="d-flex align-items-start">
                      <CalendarTodayIcon
                        style={{ fontSize: '20px', color: '#d97706', marginRight: '12px', marginTop: '2px' }}
                      />
                      <div>
                        <h6 className="fw-semibold mb-1">Renew Your Subscription</h6>
                        <small>
                          Don't lose access to your account. Renew now to continue using all features without
                          interruption.
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="login">
        <div className="container">
          <div className="row align-items-center justify-content-center m-auto min-vh-100">
            <div className="col-lg-5">
              <form onSubmit={handleLogin} className="login-form bg-white shadow p-4 rounded-3">
                <div className="mb-5 text-center">
                  <img className="w-25" src={mainlogo} alt="logo" />
                  <h1 className="fs-2 purple-dark-color text-center fw-semibold base-color mt-3">Welcome!</h1>
                  <h6 className="text-center fw-fw-normal">Sign in to your account</h6>
                </div>

                <div className="w-75 m-auto">
                  <FormControl fullWidth variant="standard">
                    <InputLabel htmlFor="username">Username</InputLabel>
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      disabled={isLoading}
                      endAdornment={
                        <InputAdornment position="end">
                          <PersonIcon />
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                  {errorMessage?.username && <div className="text-danger small mt-1">{errorMessage.username}</div>}
                </div>

                <div className="w-75 m-auto my-4">
                  <FormControl fullWidth variant="standard">
                    <InputLabel htmlFor="password">Password</InputLabel>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      disabled={isLoading}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                  {errorMessage?.password && <div className="text-danger small mt-1">{errorMessage.password}</div>}
                </div>

                <div className="text-center mt-4">
                  <Button
                    variant=""
                    type="submit"
                    disabled={isLoading}
                    className="login-btn base-color-bg text-white px-5 fw-semibold"
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      'Login'
                    )}
                  </Button>
                  <div className="mt-3 fs-6">
                    <Link to="/patient-verification" className="text-decoration-none fs-18 fw-semibold">
                      Patient
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Login;
